import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ActivityStep } from '../utils/activitySteps';

/**
 * Collapsed-by-default list of every step the assistant took this turn. Sits
 * between the thinking banner and the answer: the banner says what's happening
 * now, this says what has happened so far.
 */
export function ActivityTrail({ steps }: { steps: Array<ActivityStep> }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (steps.length === 0) {
    return null;
  }

  return (
    <div className="px-6 py-1">
      <button
        aria-expanded={isOpen}
        className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 text-[10px] leading-[170%] text-gray-500 hover:text-gray-700"
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        {isOpen ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        {t('assistant.activity.title', { count: steps.length })}
      </button>

      {isOpen && (
        <ul className="mt-1 ml-2 space-y-1 border-l border-gray-200 pl-3">
          {steps.map((step) => (
            <li className="flex items-center gap-1.5 text-[10px] leading-[170%]" key={step.id}>
              {step.status === 'done' ? (
                <Check className="size-3 shrink-0 text-gray-400" />
              ) : step.status === 'error' ? (
                <X className="size-3 shrink-0 text-gray-400" />
              ) : (
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 animate-pulse rounded-full bg-gray-400"
                />
              )}
              <span className={step.status === 'active' ? 'text-gray-600' : 'text-gray-500'}>
                {t(`assistant.activity.steps.${step.labelKey}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
